###
	Since the payload of the jobs from Greenhouse API comes as an array without much order, this
	mixin makes a @jobs object that separates the jobs into the Orgs and Departments,
	so we can access the jobs like @jobs[org][deparment], making the process of filtering
	the data easier.
	This naming convention comes from Nuro Careers team, and the value of the "Orgs" and
	"Departments" can be found on the metadata field of each job.
	The metadata inner fields are called "Custom Fields" in the GH dashboard.


	The listing component has 4 filters: Org & Department, Job Type, Location, and a Search field.
	When first launching this component the Nuro team requested that we implemented some masking
	for the values of the Org & Department, Job Type and Location. So this component also does that

	The Orgs and Departments are listed/filtered using the same <select> tag
	and the end goals is to have the front end dropdown element listing them in the following
	fashion:
	CAPITALIZED ORG
			Indented Not Capitalized Department
			Indented Not Capitalized Department
			Indented Not Capitalized Department
	CAPITALIZED ORG
			Indented Not Capitalized Department
			Indented Not Capitalized Department
			Indented Not Capitalized Department
	...

	The catch is that the closed state of this dropdown should display the Org and Department
	values without the capitalization/indentation, so we'll later stack two <select> elements
	one transparent handling the formatted dropdown values, and the other that will be
	visible displaying the closed state. The transparent will be on top, so when the user clicks
	the select element, the formatted labels will be displayed.
###


# Each metadata/custom field has an id, here are the ids we care about. We'll be
# using them to get the values of the custom fields for each job
departmentCustomFieldID = 6653805
fallbackdepartmentCustomFieldID = 4977510
orgCustomFieldID = 6644840
jobTypeCustomFieldID = 4978502

import uniq from 'lodash/uniq'
import { OrgMap, DepartmentMap, JobLocationMap, JobTypeMap } from './masking'
import { sortObjectKeys } from '/services/helpers'

# Ignore these job families
dontList = ['Ike', 'Test']

# Special space HTML characters used to ident the departments in the <select> element
spaceCharacters = "&nbsp; &nbsp;"

export default
	data: -> {
	 	# The results fetched from greenhouse
		jobs: {}
		# The array of unmasked locations
		locs: []
		# The array of unmasked jobs types
		types: []

		# Has all the job data been loaded and parsed?
		loading: true
		OrgMap, DepartmentMap, JobLocationMap, JobTypeMap
	}

	fetch: ->
	 	# The greenhouse API endpoint to fetch the job list from
		endpoint = 'https://boards-api.greenhouse.io/v1/boards/nuro/jobs/?content=true'

		# Fetch the career listing data
		{ data } = await @$axios.get endpoint
		{ @jobs, @locs, @types } = @populateJobs data.jobs

	computed:

		# Keep track of jobs that match the filter criteria given by the user
		# Anytime we have an update on the filters we have a new set of jobs to list
		# The listing component also requires us to keep count of how many jobs per
		# Org and per Department match the current filter criteria
		activeJobs: ->
			activeJobs = {}

			for org of @jobs
				# Init job family count
				orgCount = 0

				# Iterate over each department
				for department of @jobs[org]
				 	# Compute the number of active jobs in the department, and add that to
					# the job family count as well
					departmentCount = @countActiveDepartmentJobs org, department
					orgCount += departmentCount

		 			# Create an entry in the object, if not existent yet
					if !activeJobs[org] then activeJobs[org] = {departments: {}, active: 0}

					# Populate deparment count
					activeJobs[org].departments[department] = departmentCount

				# Populate job family calc
				activeJobs[org].active = orgCount

			return activeJobs

		# The list of orgs/departments used in the dropdown menu, formatted as
		# mentioned in the comment on the top of this mixin
		jobFamilyOptions: ->
		 	# Add default values
			result = [{label: "Job Family", value: ""}]

	 		# Iterate over jobs
			for org, departments of @jobs

				# Get the formatted label that will be used in the open dropdown
				# and the formatted value that will be used to identify if selected option
				# is either a job family (org) or a department
				# Using the convention "org: #{org}" for keep tracking of the organizations
				# and "org: #{org} department: #{department}"
				# This will also later be used to display the dropdown closed state value

				# Format orgs, the org labels are UPPER Case
				formattedOrg =
					label: org.toUpperCase()
					value: "org: #{org}"

				# Format departments, the department labels are double indented
				formattedDepartments = Object.keys(departments).map (department) =>

				 	# ignore null deparments
					return unless department != null and department != 'null'

					# Formatted department labels have spaces at the beginning
					{
						label: spaceCharacters + department,
						value: "department: #{department}"
						key: "org: #{org} department: #{department}"
					}

				# push values and remove null departments
				result.push formattedOrg, ...formattedDepartments

			return result

		# The list of job families/departments used in the select closed state
		jobFamilyOptionsUnformatted: -> [@familyOrDepartmentUnformatted]

		# Merge default location with the masked list of locations
		locations: ->
			uniq [{label: "Location/Office", value: ""}].concat(
				@locs.map (loc) => @maskLocation loc)

		# Merge default job type with list of job types: Full Time || Internship ...
		jobTypes: ->
			uniq [{ label: "Type", value: "" }].concat(
				@types.map (type) => @maskType type)

		# Are there jobs that match the current filtering criteria?
		hasJobs: ->
			for jobFamily of @activeJobs
				return true if @activeJobs[jobFamily].active > 0
			return false

		# Are there any active filters at the moment?
		hasFilters: ->
			!!@activeOrg || !!@activeDepartment || !!@formBind.location || !!@formBind.jobType || !!@formBind.query

		# The selected org
		# formBind is defined in careers-listing-v2/index.vue
		# value can be of two formats:
		# - "org: <org-name>"
		# - "department: <department-name>"
		# we parse that and get the selected org value or return an empty string if
		# the current selected option is a department
		activeOrg: ->
			value = @formBind.familyOrDepartment
			rawValue = value.split(':')[1]?.trim()

			if value.includes("org:") then rawValue
			else ""

		# The selected department, same logic as above, without loss of generality
		activeDepartment: ->
			value = @formBind.familyOrDepartment
			rawValue = value.split(':')[1]?.trim()

			if value.includes("org:") then ""
			else rawValue

		# The value of either family or job family that is displayed on select closed state
		familyOrDepartmentUnformatted: ->
			value = @formBind.familyOrDepartment
			if value == '' then "Job Family"
			else value.split(':')[1]?.trim()

	watch:
		jobs: (jobs) ->

			# Once jobs are fetched, do stuff to the jobs descriptions
			# (which come as HTML with special characters) so we can properly query for strings in it
			# bail if we don't access to the DOM, because we will need it to parse data
			return unless process.client and @loading

			# Iterate over each job
			for org of @jobs
				for department of @jobs[org]
					for i in [0..@jobs[org][department].length-1]
						job = @jobs[org][department][i]

						# Create an HTML element, add the jobs description as inner HTML
						# This will transform special HTML characters like "&rsaquo;" into ">"
						div = document.createElement 'div'
						div.innerHTML = job.description
						innerText = div.innerText

						# Now passing the just transformed HTML string that has proper utf characters
						# as innerHTML so we can parse out the HTML tags from the text
						div.innerHTML = innerText
						innerText = div.innerText
						@jobs[org][department][i].description = innerText

	 		# If the jobs have been fetched, on check if there are hits that match filters
			# If the block had its mode set to "Early Careers", but no initial hits
			# found, then fallback to default behavior
			if Object.keys(jobs).length then @$nextTick => if @matchDropdownOrSearch and !@hasJobs
				@clearFilters()
				@filterBehavior = 'default'

	 		# Has finished loading jobs and parsing descriptions
			# should display jobs now
			@loading = false

	methods:
		# Get the job org custom field value for an entry
		getJobOrg: (job) ->
			orgCustomFieldIndex = job.metadata.findIndex (meta) -> meta.id == orgCustomFieldID
			job.metadata[orgCustomFieldIndex].value

		# Get the job department custom field value for an entry
		# We're using a fallback field if there's no value in the department custom field we care about
		# https://bkwld.slack.com/archives/C01K8JPJ6TA/p1671566683921899
		getJobDepartment: (job) ->
			departmentCustomFieldIndex = job.metadata.findIndex (meta) -> meta.id == departmentCustomFieldID
			fallbackDeptCustomFieldIndex = job.metadata.findIndex (meta) -> meta.id == fallbackdepartmentCustomFieldID
			{
				department: job.metadata[departmentCustomFieldIndex].value
				fallback: job.metadata[fallbackDeptCustomFieldIndex].value
			}

		# Get the job type custom field value for an entry
		getJobType: (job) ->
			jobTypeIndex = job.metadata.findIndex (meta) -> meta.id == jobTypeCustomFieldID
			job.metadata[jobTypeIndex].value

		# Populate the @jobs object grouping entries into orgs, then departments
		populateJobs: (data) ->
			jobs = {}
			locs = []
			types = []

			data.forEach (job) =>
				# Get the unmasked custom field values
				orgUnmasked = @getJobOrg job
				jobType = @getJobType job
				{	department: departmentUnmasked, fallback: fallbackUnmasked } = @getJobDepartment job

				# Ignore jobs that don't have this meta fields
				if !orgUnmasked or ( !departmentUnmasked && !fallbackUnmasked ) then return

				org = @maskOrg orgUnmasked
				dept = @maskDept(  ( departmentUnmasked || fallbackUnmasked) )

				# Create objects if non existent
				if !jobs[org] then jobs[org] = {[dept]: []}
				else if !jobs[org][dept] then jobs[org][dept] = []

				# Exchange fields for later usage
				job.description = job.content
				job.content = undefined

				# Add job to object
				jobs[org][dept].push job

				# Push location to array if not existant yet
				locs.push job.location.name if !locs.includes job.location.name

				# Push time type to array if not existant yet
				types.push jobType if !types.includes jobType

			# Filter null values and remove duplicates
			locs = uniq locs.filter (loc) -> !!loc
			types = uniq types.filter (loc) -> !!loc

			# Sort jobs alphabetically
			jobs = @sortJobs jobs

			return { jobs, locs, types }

		# Sort orgs, departments, and jobs titles alphabetically
		sortJobs: (jobs) ->
			# Sort job family
			jobs = sortObjectKeys(jobs)

			# Sort departments
			Object.keys(jobs).forEach (org) ->
				jobs[org] = sortObjectKeys(jobs[org])

				# Sort job titles
				Object.keys(jobs[org]).forEach (department) -> jobs[org][department].sort()

			return jobs

		# Apply and retrieve values given masks provided by Nuro recruiting team
		# Location
		maskLocation: (location) -> JobLocationMap.forward location
		unmaskLocation: (location) -> JobLocationMap.backward location
		# Job Type
		maskType: (type) -> JobTypeMap.forward type
		unmaskType: (type) -> JobTypeMap.backward type
		# Org
		maskOrg: (org) -> OrgMap.forward org
		unmaskOrg: (org) -> OrgMap.backward org
		# Department
		maskDept: (dept) -> DepartmentMap.forward dept
		unmaskDept: (dept) -> DepartmentMap.backward dept

		# Count the number of jobs active in a department
		countActiveDepartmentJobs: (org, department) ->
			count = 0
			@jobs[org][department].forEach (job) => if @jobMatch job then count++
			return count

		# Check if job matches selected job family
		jobMatchOrg: (job) ->
			org = @getJobOrg job
			return !@activeOrg or @unmaskOrg(@activeOrg).includes org

		# Check if job matches selected department
		jobMatchDepartment: (job) ->
			{	department: departmentUnmasked, fallback: fallbackUnmasked } = @getJobDepartment job
			department = departmentUnmasked || fallbackUnmasked
			return !@activeDepartment or @unmaskDept(@activeDepartment).includes department

		# Check if job matches selected location
		jobMatchLocation: (job) ->
			return true if !@formBind.location
			unmasked = @unmaskLocation @formBind.location
			unmasked.includes job.location.name

		# Check if job matches selected job type
		jobMatchType: (job) ->
			return true if !@formBind.jobType
			jobType = @getJobType job
			unmasked = @unmaskType @formBind.jobType
			unmasked.includes jobType

		# Check if job matches active query
		jobMatchQuery: (job) ->
			match = @formBind.query.toLowerCase()
			org = @maskOrg @getJobOrg job

			{	department: departmentUnmasked, fallback: fallbackUnmasked } = @getJobDepartment job
			department = @maskDept(  ( departmentUnmasked || fallbackUnmasked) )

			return (
				!@formBind.query or
				job.title.toLowerCase().includes(match) or
				org.toLowerCase().includes(match) or
				department.toLowerCase().includes(match) or
				job.description.toLowerCase().includes(match)
			)

		# Match job depending on filtering mode
		jobMatch: (job) ->
			if @matchDropdownOrSearch then @jobMatchEarlyCareer job
			else @jobMatchDefault job

		# Check if job matches all criteria
		jobMatchDefault: (job) ->
			return true if (
				@jobMatchOrg(job) and
				@jobMatchDepartment(job) and
				@jobMatchLocation(job) and
				@jobMatchType(job) and
				@jobMatchQuery( job )
			)

		# Check if job matches html select filter values OR matches the description/title search input value
		jobMatchEarlyCareer: (job) ->
			if !@formBind.query
				return true if (
					@jobMatchOrg(job) and
					@jobMatchDepartment(job) and
					@jobMatchLocation(job) and
					@jobMatchType(job)
				)
			else
				return true if ( @jobMatchQuery job ) or (
					( !!@activeOrg and @jobMatchOrg(job) ) or
					( !!@activeDepartment and @jobMatchDepartment(job) ) or
					( !!@formBind.location and @jobMatchLocation(job)  ) or
					( !!@formBind.jobType and @jobMatchType(job) )
				)

		# Clear filters
		clearFilters: ->
			@formBind =
				location: ''
				query: ''
				jobType: ''
				familyOrDepartment: ''
